import { type FrameBuilderConfiguration } from './configuration'
import { type FrameBuilderState } from './state'

type FrameField = 'frame' | 'bg'

type ItemType =
  | 'profile'
  | 'texture'
  | 'passepartout_size'
  | 'passepartout_color'
  | 'glass'
  | 'spacer_size'
  | 'spacer_color'
  | 'extra_service'

interface SkuMapValue {
  _type: ItemType
  id: string
}

type SkuMap = Record<string, SkuMapValue>

export const defaultSectionSeparator = ';'

export const defaultSectionValueSeparator = ':'

export const defaultFieldSeparator = '-'

export const defaultValueSeparator = ':'

export const defaultSizeValueSeparator = 'x'

export const fieldSeparator = '|'

export const valueSeparator = '+'

export const passepartoutSkuPrefix = 'PP'

/**
 * Gets selected item values from frame builder state.
 */
const getFrameSelectedValues = (
  state: FrameBuilderState,
  valueSeparator = defaultValueSeparator,
  sizeValueSeparator = defaultSizeValueSeparator,
) => {
  const trimValueSeparatorRegex = new RegExp(
    `^[${valueSeparator}]+|[${valueSeparator}]+$`,
    'g',
  )

  const passepartoutSku = [
    state.passepartout.size?.sku ?? '',
    state.passepartout.color?.sku ?? '',
  ]
    .join(valueSeparator)
    .replace(trimValueSeparatorRegex, '')

  return [
    state.artwork.width && state.artwork.height
      ? [state.artwork.width, state.artwork.height].join(sizeValueSeparator)
      : null,
    state.profile?.sku,
    state.texture?.sku,
    passepartoutSku.length > 0
      ? `${passepartoutSkuPrefix}${passepartoutSku}`
      : null,
    state.glass?.sku,
    ...(state.extras.active
      ? [
          ...(state.extras.extraServices?.map(
            (extraService) => extraService.sku,
          ) ?? []),
          [
            state.extras.spacer.size?.sku ?? '',
            state.extras.spacer.color?.sku ?? '',
          ]
            .join(valueSeparator)
            .replace(trimValueSeparatorRegex, ''),
        ]
      : []),
  ].filter(Boolean) as string[]
}

/**
 * Generates selected item value string from frame builder state.
 */
const getSelectedFrameValueString = (
  state: FrameBuilderState,
  fieldSeparator = defaultFieldSeparator,
  valueSeparator = defaultValueSeparator,
  sizeValueSeparator = defaultSizeValueSeparator,
) => {
  return getFrameSelectedValues(state, valueSeparator, sizeValueSeparator).join(
    fieldSeparator,
  )
}

/**
 * Generates a string from frame builder state.
 */
export const getFrameString = (
  state: FrameBuilderState,
  sectionSeparator = defaultSectionSeparator,
  sectionValueSeparator = defaultSectionValueSeparator,
  fieldSeparator = defaultFieldSeparator,
  valueSeparator = defaultValueSeparator,
  sizeValueSeparator = defaultSizeValueSeparator,
) => {
  const backgroundColor = state.artwork?.backgroundColor?.current
  const selectedValueString = getSelectedFrameValueString(
    state,
    fieldSeparator,
    valueSeparator,
    sizeValueSeparator,
  )

  const frameParts: [FrameField, string] = ['frame', selectedValueString]
  const backgroundParts: [FrameField, string] = ['bg', backgroundColor ?? '']

  return [
    frameParts.join(sectionValueSeparator),
    backgroundColor ? backgroundParts.join(sectionValueSeparator) : null,
  ]
    .filter(Boolean)
    .join(sectionSeparator)
    .toLowerCase()
}

/**
 * Gets SKU to item mapping.
 */
function getSkuItemMap(configuration: FrameBuilderConfiguration): SkuMap {
  const profiles: SkuMap[] = configuration.profiles.map((profile) => ({
    [profile.sku.toLowerCase()]: {
      _type: 'profile',
      id: profile._id,
    },
  }))
  const textures: SkuMap[] = configuration.textures.map((texture) => ({
    [texture.sku.toLowerCase()]: {
      _type: 'texture',
      id: texture._id,
    },
  }))
  const passepartoutSizes: SkuMap[] = configuration.passepartout.sizes.map(
    (size) => ({
      [size.sku.toLowerCase()]: {
        _type: 'passepartout_size',
        id: size._id,
      },
    }),
  )
  const passepartoutColors: SkuMap[] = configuration.passepartout.colors.map(
    (color) => ({
      [color.sku.toLowerCase()]: {
        _type: 'passepartout_color',
        id: color._id,
      },
    }),
  )
  const glass: SkuMap[] = configuration.glass.map((glass) => ({
    [glass.sku.toLowerCase()]: {
      _type: 'glass',
      id: glass._id,
    },
  }))
  const spacerSizes: SkuMap[] = configuration.spacer.sizes.map((size) => ({
    [size.sku.toLowerCase()]: {
      _type: 'spacer_size',
      id: size._id,
    },
  }))
  const spacerColors: SkuMap[] = configuration.spacer.colors.map((color) => ({
    [color.sku.toLowerCase()]: {
      _type: 'spacer_color',
      id: color._id,
    },
  }))
  const extraServices: SkuMap[] = configuration.extraServices.map(
    (extraService) => ({
      [extraService.sku.toLowerCase()]: {
        _type: 'extra_service',
        id: extraService._id,
      },
    }),
  )

  return Object.assign(
    {},
    ...[
      ...profiles,
      ...textures,
      ...passepartoutSizes,
      ...passepartoutColors,
      ...glass,
      ...spacerSizes,
      ...spacerColors,
      ...extraServices,
    ],
  )
}

/**
 * Returns a reducer method for any array of SKUs.
 */
const getSkuReducer = (configuration: FrameBuilderConfiguration) => {
  const skuItemMap = getSkuItemMap(configuration)

  return (state: FrameBuilderState, sku: string): FrameBuilderState => {
    const item: SkuMapValue | undefined = skuItemMap[sku]

    switch (item?._type) {
      case 'profile': {
        return {
          ...state,
          profile:
            configuration.profiles.find((profile) => profile._id === item.id) ||
            state.profile,
        }
      }

      case 'texture': {
        return {
          ...state,
          texture:
            configuration.textures.find((texture) => texture._id === item.id) ||
            state.texture,
        }
      }

      case 'passepartout_size': {
        const updatedPassepartout = state.passepartout
        updatedPassepartout.size =
          configuration.passepartout.sizes.find(
            (size) => size._id === item.id,
          ) || updatedPassepartout.size

        return {
          ...state,
          passepartout: updatedPassepartout,
        }
      }

      case 'passepartout_color': {
        const updatedPassepartout = state.passepartout
        updatedPassepartout.color =
          configuration.passepartout.colors.find(
            (color) => color._id === item.id,
          ) || updatedPassepartout.color

        return {
          ...state,
          passepartout: updatedPassepartout,
        }
      }

      case 'glass': {
        return {
          ...state,
          glass:
            configuration.glass.find((glass) => glass._id === item.id) ||
            state.glass,
        }
      }

      case 'spacer_size': {
        const updatedExtras = state.extras
        updatedExtras.spacer.size =
          configuration.spacer.sizes.find((size) => size._id === item.id) ||
          updatedExtras.spacer.size

        return {
          ...state,
          extras: updatedExtras,
        }
      }

      case 'spacer_color': {
        const updatedExtras = state.extras
        updatedExtras.spacer.color =
          configuration.spacer.colors.find((color) => color._id === item.id) ||
          updatedExtras.spacer.color

        return {
          ...state,
          extras: updatedExtras,
        }
      }

      case 'extra_service': {
        let updatedExtraServices = state.extras.extraServices ?? []

        // Add extra service to the list, if it was found
        const newExtraService = configuration.extraServices.find(
          (extraService) => extraService._id === item.id,
        )

        if (newExtraService) {
          updatedExtraServices = [...updatedExtraServices, newExtraService]
        }

        return {
          ...state,
          extras: {
            ...state.extras,
            extraServices: updatedExtraServices,
          },
        }
      }

      default: {
        return state
      }
    }
  }
}

/**
 * Returns a reducer method for any state string value (e.g, "frame", "UV99", "10x20" or "PP5CM+WHITE").
 */
const getStateStringValueReducer = (
  configuration: FrameBuilderConfiguration,
  valueSeparator: string,
  sizeValueSeparator: string,
) => {
  const passepartoutPrefixRegex = new RegExp(
    `^${passepartoutSkuPrefix.toLowerCase()}`,
    'g',
  )
  const sizeRegex = new RegExp(
    `^[0-9.]+[${sizeValueSeparator}]{1}[0-9.]+$`,
    'g',
  )
  const passepartoutRegex = new RegExp(
    `^${passepartoutSkuPrefix.toLowerCase()}.+([${valueSeparator}]{1}.+)?$`,
    'g',
  )
  const pairRegex = new RegExp(`^.+[${valueSeparator}]{1}.+$`, 'g')
  const skuReducer = getSkuReducer(configuration)

  return (state: FrameBuilderState, valuePart: string): FrameBuilderState => {
    // Check if value has 2 size values
    if (valuePart.includes(sizeValueSeparator) && sizeRegex.test(valuePart)) {
      // Split into numbers
      const [width, height] = valuePart.split(sizeValueSeparator)
      const updatedArtwork = state.artwork
      updatedArtwork.width = Number(width)
      updatedArtwork.height = Number(height)
      return {
        ...state,
        artwork: updatedArtwork,
      }
    }

    // Check if value has a pair of SKUs with passepartout prefix
    if (passepartoutRegex.test(valuePart)) {
      // Remove prefix and split into SKUs, if more than 1 is present
      const passepartoutSkus = valuePart
        .replace(passepartoutPrefixRegex, '')
        .split(valueSeparator)

      // Resolve passepartout size and color SKUs
      return passepartoutSkus.reduce(skuReducer, state)
    }

    // Check if value has a pair of SKUs (it is asumed that no SKUs contain the separator)
    if (pairRegex.test(valuePart)) {
      const skus = valuePart.split(valueSeparator)

      // Resolve spacer size and spacer color SKUs
      return skus.reduce(skuReducer, state)
    }

    // Resolve profile, texture, glass or any extra service SKU
    return [valuePart].reduce(skuReducer, state)
  }
}

/**
 * Returns a reducer method for any state string section (e.g., "frame:uv99|crop|spacer-1+spacer-white").
 */
const getStateStringSectionReducer =
  (
    configuration: FrameBuilderConfiguration,
    sectionValueSeparator: string,
    fieldSeparator: string,
    valueSeparator: string,
    sizeValueSeparator: string,
  ) =>
  (currentState: FrameBuilderState, field: string) => {
    const fieldParts = field.split(sectionValueSeparator)
    const [name, value] = [
      fieldParts[0] as FrameField,
      fieldParts.slice(1).join(sectionValueSeparator),
    ]

    switch (name) {
      case 'bg': {
        const updatedArtwork = currentState.artwork

        updatedArtwork.backgroundColor = {
          current: value,
          predefinedColors:
            updatedArtwork.backgroundColor?.predefinedColors ?? [],
        }

        return {
          ...currentState,
          artwork: updatedArtwork,
        }
      }

      case 'frame': {
        return value
          .split(fieldSeparator)
          .reduce(
            getStateStringValueReducer(
              configuration,
              valueSeparator,
              sizeValueSeparator,
            ),
            currentState,
          )
      }

      default: {
        return currentState
      }
    }
  }

/**
 * Parses state string into frame builder state.
 */
export const getStateFromFrameString = (
  stateString: string,
  configuration: FrameBuilderConfiguration,
  defaultState: FrameBuilderState,
  sectionSeparator = defaultSectionSeparator,
  sectionValueSeparator = defaultSectionValueSeparator,
  fieldSeparator = defaultFieldSeparator,
  valueSeparator = defaultValueSeparator,
  sizeValueSeparator = defaultSizeValueSeparator,
): FrameBuilderState => {
  const state = Object.assign({}, defaultState)

  if (stateString) {
    // Clear default extra service list, since state string contains all necessary extra service SKUs
    state.extras.extraServices = []
  }

  // Split into sections and reduce
  const sections = stateString.split(sectionSeparator)
  const stateStringSectionReducer = getStateStringSectionReducer(
    configuration,
    sectionValueSeparator,
    fieldSeparator,
    valueSeparator,
    sizeValueSeparator,
  )

  return sections.reduce(stateStringSectionReducer, state)
}
